import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { viewAttachment } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { StatusChip } from "@/components/RoadmapStrip";
import { toast } from "sonner";
import { FolderClosed, Upload, FileText, Download, HelpCircle, Lock, ArrowRight, BadgeCheck, Landmark, Briefcase, ShieldCheck, GraduationCap, Folder } from "lucide-react";
import { STATUS_LABEL } from "@/pages/portal/portalUtils";

const UPLOAD_LOCKED = new Set(["complete", "waived"]);
const CAT_ORDER = ["Licensing", "Financial", "Background", "Business", "Training", "Other"];
const CAT_ICON = { Licensing: BadgeCheck, Financial: Landmark, Background: ShieldCheck, Business: Briefcase, Training: GraduationCap, Other: Folder };

export default function PortalDocuments({ goTo }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => { api.get("/portal/documents").then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);
  if (items === null) return <p className="text-sm text-slate-400">Loading your documents…</p>;
  const cats = [...new Set(items.map((i) => i.category || "Other"))].sort((a, b) => CAT_ORDER.indexOf(a) - CAT_ORDER.indexOf(b));
  const openTask = (id) => goTo?.("tasks", null, { taskId: id });

  return (
    <div className="space-y-8" data-testid="portal-documents">
      <p className="text-sm text-slate-600">Every document your program needs, organised by category. Your district team reviews each one.</p>
      {items.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="portal-documents-empty">
          <FolderClosed className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No document requests yet. They'll appear here as your program progresses.</p>
        </div>
      )}
      {cats.map((c) => {
        const Icon = CAT_ICON[c] || Folder;
        return (
          <section key={c} data-testid={`portal-doc-cat-${c.toLowerCase()}`}>
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#006090]" />{c}</h2>
            <div className="space-y-3">{items.filter((i) => (i.category || "Other") === c).map((it) => it.kind === "task"
              ? <TaskDocRow key={`t-${it.task_id}`} item={it} onOpen={() => openTask(it.task_id)} />
              : <DocRow key={it.requirement_id} item={it} onChanged={load} onOpen={it.task_id ? () => openTask(it.task_id) : null} />)}</div>
          </section>
        );
      })}
    </div>
  );
}

const TASK_TONE = { completed: "text-emerald-700 bg-emerald-50 border-emerald-200", submitted: "text-[#006090] bg-sky-50 border-sky-200", needs_attention: "text-rose-700 bg-rose-50 border-rose-200" };

function TaskDocRow({ item, onOpen }) {
  const label = item.is_planned ? "Not Yet Requested" : item.files.length === 0 && item.status !== "completed" ? "Not Yet Submitted" : STATUS_LABEL[item.status] || item.status;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-start justify-between gap-3" data-testid={`portal-doc-task-${item.task_id}`}>
      <div className="min-w-0">
        <p className="font-semibold text-slate-900">{item.title}</p>
        {item.files.map((f) => <p key={f.id} className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />{f.filename} · Submitted {new Date(f.uploaded_at).toLocaleDateString()}</p>)}
        {!item.is_planned && <button onClick={onOpen} className="mt-2 text-xs font-semibold text-[#006090] inline-flex items-center gap-1" data-testid={`portal-doc-open-task-${item.task_id}`}>{item.status === "needs_attention" ? "Upload replacement" : "Open task"}<ArrowRight className="w-3 h-3" /></button>}
      </div>
      <span className={`shrink-0 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${TASK_TONE[item.status] || "text-slate-600 bg-slate-50 border-slate-200"}`} data-testid={`portal-doc-task-status-${item.task_id}`}>{label}</span>
    </div>
  );
}

function DocRow({ item, onChanged, onOpen }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const locked = UPLOAD_LOCKED.has(item.status);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25 MB)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("requirement_id", item.requirement_id);
      fd.append("file", file);
      await api.post("/portal/documents/upload", fd);
      toast.success("Uploaded — your recruiter will review it.");
      onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const download = async (att) => {
    try { await viewAttachment(`/portal/attachments/${att.attachment_id}/file`, att.filename); } catch (err) { toast.error(apiError(err)); }
  };
  const count = item.sensitive ? item.capital?.document_count : item.evidence?.length;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`portal-doc-${item.requirement_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">{item.title}</p>
          {item.phase_name && <p className="text-[11px] text-slate-400 uppercase tracking-wide mt-0.5">{item.phase_name}</p>}
          {item.instructions && <p className="mt-1 text-sm text-slate-500">{item.instructions}</p>}
          {item.key_contact_topic && <p className="mt-1 text-xs text-slate-400 inline-flex items-center gap-1"><HelpCircle className="w-3.5 h-3.5" /> Need help? Ask about: {item.key_contact_topic}</p>}
        </div>
        {!count && !locked ? <span className="shrink-0 text-[11px] font-semibold border rounded-full px-2 py-0.5 text-slate-600 bg-slate-50 border-slate-200">Not Yet Submitted</span> : <StatusChip status={item.status} />}
      </div>
      {item.evidence?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">{item.evidence.map((e) => (
          <button key={e.id} onClick={() => download(e)} data-testid={`portal-doc-file-${e.id}`}
            className="inline-flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 hover:border-[#006090] hover:text-[#006090] transition-colors">
            <FileText className="w-3.5 h-3.5" /> {e.filename || "Uploaded file"} <Download className="w-3 h-3" />
          </button>))}</div>
      )}
      {item.sensitive && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 space-y-1" data-testid={`portal-doc-sensitive-${item.requirement_id}`}>
          {item.capital?.document_count > 0 ? (
            <p className="text-sm text-slate-800" data-testid={`portal-doc-sensitive-status-${item.requirement_id}`}>
              <FileText className="w-3.5 h-3.5 inline mr-1 text-slate-500" />
              {item.capital.document_count} document(s) received{item.capital.last_submitted_at ? ` · Submitted ${new Date(item.capital.last_submitted_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}` : ""}
              {" · "}<strong className={item.capital.status === "verified" ? "text-emerald-600" : item.capital.status === "rejected" || item.capital.status === "expired" ? "text-rose-600" : "text-[#006090]"}>
                {item.capital.status === "verified" ? "Verified" : item.capital.status === "rejected" ? "Needs Attention" : item.capital.status === "expired" ? "Expired — please resubmit" : "In Review"}</strong>
            </p>
          ) : <p className="text-sm text-slate-700">Not yet submitted</p>}
          <p className="flex items-start gap-1.5"><Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />For your privacy, financial documents are stored securely and can only be opened by your recruiting team — they can't be downloaded from the portal.</p>
          {(item.capital?.status === "rejected" || item.capital?.status === "expired") && <p className="text-rose-700">Please upload an updated statement.</p>}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" className="hidden" onChange={upload} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt,.xlsx,.csv" data-testid={`portal-doc-input-${item.requirement_id}`} />
        {locked ? <p className="text-xs text-emerald-600 font-medium">Verified — no further action needed.</p> : (
          <Button onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" size="sm" data-testid={`portal-doc-upload-${item.requirement_id}`} className="border-[#006090]/30 text-[#006090] hover:bg-[#006090]/5">
            <Upload className="w-4 h-4 mr-1.5" /> {busy ? "Uploading…" : count ? "Upload another" : "Upload document"}
          </Button>
        )}
        {onOpen && <button onClick={onOpen} className="text-xs font-semibold text-[#006090] inline-flex items-center gap-1" data-testid={`portal-doc-open-task-${item.task_id}`}>Open related task<ArrowRight className="w-3 h-3" /></button>}
      </div>
    </div>
  );
}
